import Context from "../../../context/Context";
import { IRecurrenceOptions } from "../More/Actions/BookWorkspace/BookingRecurrence";
import { IEventInfo, IReservationInfo } from "../More/Actions/BookWorkspace/WorkspaceReservation/workspaceReservationUtil";
import { BookingOperation, BookingType } from "../../../util/calendarUtil";

function getRecurrenceRule(options: IRecurrenceOptions) {
  let { endDate, type, days } = options;
  const uc = type.toUpperCase();
  const until = formatDate(endDate);
  let daysByDay = [];
  if (uc === "WEEKLY") {
    const daysOfWeek = {};
    daysOfWeek[0] = 'SU';
    daysOfWeek[1] = 'MO';
    daysOfWeek[2] = 'TU';
    daysOfWeek[3] = 'WE';
    daysOfWeek[4] = 'TH';
    daysOfWeek[5] = 'FR';
    daysOfWeek[6] = 'SA';
    const daysByNum = days;
    daysByNum.forEach((num) => {
      daysByDay.push(daysOfWeek[num])
    })
    return `RRULE:FREQ=${uc};UNTIL=${until};INTERVAL=${options.interval};WKST=SU;BYDAY=${daysByDay}`;
  } else if (uc === "DAILY") {
    return `RRULE:FREQ=${uc};UNTIL=${until}`;
  }
}

const openCalendar =(bookingOperation: BookingOperation, prodid: string) => {
  const calendar = [];
  if (bookingOperation === "removeBooking") {
    calendar.push(...[
      "BEGIN:VCALENDAR",
      "PRODID:" + prodid,
      "VERSION:2.0",
      "METHOD:CANCEL"
    ]);
  } else if (bookingOperation === "updateBooking") {
    calendar.push(...[
      "BEGIN:VCALENDAR",
      "PRODID:" + prodid,
      "VERSION:2.0",
      "METHOD:PUBLISH"
    ]);
  } else {
    calendar.push(...[
      "BEGIN:VCALENDAR",
      "PRODID:" + prodid,
      "VERSION:2.0",
      "METHOD:PUBLISH"
    ]);
  }
  return calendar;
}
const openEvent = (bookingOperation: BookingOperation, uid: string, dateNow: string, sequence: number = 1) => {
  const event = [
    "BEGIN:VEVENT",
    "UID:" + uid,
    "DTSTAMP:" + dateNow
  ];
  switch (bookingOperation) {
    case "addBooking":
      event.push(...["SEQUENCE:0"]);
      break;
    case "removeBooking":
      event.push(...[
        "STATUS:CANCELLED",
        "SEQUENCE:" + sequence
      ]);
      break;
    case "updateBooking":
      event.push(...[
        "STATUS:NEEDS-ACTION",
        "SEQUENCE:" + sequence
      ])
      break;
  }
  return event;
}
const closeEvent = () => (["END:VEVENT"]);

const chkStr = (v) => {
  if (typeof v !== "string") return "";
  return v;
}

const formatDate = (date: Date): string => {
  if (date === undefined || date === null) return null;
  if (typeof date === "number") {
    date = new Date(date);
  }
  const opts = { zulu: false, milliseconds: false }; // keep local time
  const lib = Context.getInstance().lib;
  const v: string = lib.dojo.stamp.toISOString(date, opts);
  return v.substring(0, v.lastIndexOf("-")).replace(/-/g,"").replace(/:/g,"");
}
export interface IICSData {
  blob: Blob,
  data: string,
  href: string,
  filename: string,
  type: BookingType
}
export function makeIcsInfo(
  eventInfo: IReservationInfo | IEventInfo,
  filename: string,
  bookingOperation: BookingOperation,
  type: BookingType = "single"
): Promise<IICSData> {
  const promise = new Promise<IICSData>((resolve,reject) => {
    try {
      const icsInfo = {
        blob: null,
        data: null,
        href: null,
        filename: filename || "event.ics",
        type
      }
      const data = icsInfo.data = makeIcsString(eventInfo, bookingOperation, type === "occurrence");
      if (data && FileReader && Blob) {
        const blob = icsInfo.blob = new Blob([data], {type:"text/calendar"});
        const reader = new FileReader();
        reader.onload = function(event) {
          icsInfo.href = event.target.result;
          resolve(icsInfo);
        };
        reader.onerror = function(err) {
          reject(err);
        }
        reader.readAsDataURL(blob);
      } else {
        resolve(icsInfo);
      }
    } catch(ex) {
      reject(ex);
    }
  });
  return promise;
}

function makeIcsString(eventInfo: IReservationInfo | IEventInfo, bookingOperation: BookingOperation, occurrence: boolean) {
  const prodid = "ArcGIS Indoors"; // TODO?
  const summary = chkStr(eventInfo.name);
  const description = chkStr(eventInfo.description);
  const hyperlink = chkStr(eventInfo.hyperlink);
  const location = chkStr(eventInfo.location);
  const dateNow = new Date();
  const formattedDate = formatDate(dateNow);
  let uid = eventInfo.uid;
  let dateStart = formatDate(eventInfo.date_start);
  let dateEnd = formatDate(eventInfo.date_end);
  // recurrence_id here does not refer to the series id as defined in the workspace reservation
  // schema; instead, it refers to a unique occurrence of an event within a recurring series. It's
  // value is the original start time of the occurrence and is used when updating/deleting an occurrence.
  // https://icalendar.org/iCalendar-RFC-5545/3-8-4-4-recurrence-id.html
  const recurrence_id = occurrence && (bookingOperation === "removeBooking" || bookingOperation === "updateBooking")
    ? formatDate(eventInfo.occurrence_id)
    : null;

  let geo = eventInfo.geo;
  if (!geo) {
    let geometry = eventInfo.geometry;
    if (geometry && geometry.type === "polygon") {
      geometry = (geometry as __esri.Polygon).centroid;
    }
    if (geometry && geometry.type === "point") {
      const point = geometry as __esri.Point;
      if (typeof point.latitude === "number" && typeof point.longitude === "number") {
        geo = point.latitude + ";" + point.longitude;
      }
    }
  }
  geo = chkStr(geo);

  if (summary.length === 0 || !dateStart) {
    return null; // not enough info
  }

  const options = "recurrenceOptions" in eventInfo && eventInfo.recurrenceOptions;

  let ics = openCalendar(occurrence ? "updateBooking" : bookingOperation, prodid);
  const isSeriesCancelled = options && !occurrence && bookingOperation === "removeBooking";
  const sequence = eventInfo.sequence ?? 1;
  if (options) { // part of a series
    // since this booking operation is part of a series we also need to update the series    
    ics.push(...openEvent(occurrence ? "updateBooking" : bookingOperation, uid, formattedDate, sequence));
    ics.push(getRecurrenceRule(options));
    if (eventInfo.ex_date?.length > 0 && !isSeriesCancelled) {
      ics.push(`EXDATE:${[...new Set<string>(eventInfo.ex_date.map(d => formatDate(d)))].join(",")}`);
    }  
    ics.push(...addEventDetails(formatDate(eventInfo.series_start), formatDate(eventInfo.series_end)));
    ics.push(...closeEvent());
  } else {
    ics.push(...openEvent(bookingOperation, uid, formattedDate));
    ics.push(...addEventDetails());
    ics.push(...closeEvent());
  }

  // add individual occurrence update
  if (bookingOperation === "addBooking" || isSeriesCancelled) {
    eventInfo.modified.forEach((occurrence, i) => {
      const { original_date_start: recurrence_id, date_start, date_end } = occurrence;
      if (isSeriesCancelled) {
        ics.push(...openEvent("removeBooking", uid, formattedDate, sequence + i + 1));
        ics.push(`RECURRENCE-ID:${formatDate(recurrence_id)}`)
        ics.push(...addEventDetails(formatDate(date_start), formatDate(date_end)));
        ics.push(...closeEvent());
      } else if (recurrence_id && dateStart && dateEnd) {
        ics.push(...openEvent("updateBooking", uid, formattedDate, sequence + i + 1));
        ics.push(`RECURRENCE-ID:${formatDate(recurrence_id)}`)
        ics.push(...addEventDetails(formatDate(date_start), formatDate(date_end)));
        ics.push(...closeEvent());
      }
    });
  } else if (occurrence && recurrence_id && (bookingOperation === "updateBooking" || bookingOperation === "removeBooking")) {
    // add only the updated/canceled event
    ics.push(...openEvent(bookingOperation, uid, formattedDate, sequence + 1));
    ics.push(`RECURRENCE-ID:${recurrence_id}`);
    ics.push(...addEventDetails());
    ics.push(...closeEvent());
  }

  ics = ics.concat([
    "END:VCALENDAR"
  ]);

  // "CLASS:PUBLIC", // TODO?
  // "TRANSP:TRANSPARENT",   // TODO?

  const delimiter = "\n";
  const data = ics.join(delimiter);
  return data;

  function addEventDetails(start?: string, end?: string) {
    const details = [];
    if (eventInfo?.bookingType === "hotel") details.push("X-MICROSOFT-CDO-BUSYSTATUS:FREE");
    if (start || dateStart) details.push(`DTSTART:${start || dateStart}`);
    if (end || dateEnd) details.push(`DTEND:${end || dateEnd}`);
    if (summary.length > 0) details.push("SUMMARY:" + summary);
    if (location.length > 0) details.push("LOCATION:" + location);
    if (geo.length > 0) details.push("GEO:" + geo);
    if (description.length > 0) details.push("DESCRIPTION:" + description);
    if (hyperlink.length > 0) details.push(hyperlink);
    return details;
  }
}
